import React, { useEffect, useState } from 'react';
import { InputAdornment, TextField, MenuItem, Select } from '@mui/material';
import { format } from 'date-fns-tz';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import { triggerNotification } from '../../utils/NotificationContext';
import {
  convertToCurrency,
  convertToCurrencyWithoutSign,
  convertToPercentageWithDecimal,
  convertToRoundOffWithNumberOfDecimals
} from '../../utils/Utils';
import {
  DOLLAR_FORMAT_COLUMNS,
  COMMA_FORMAT_COLUMNS,
  ZERO_DECIMAL_FORMAT_COLUMNS,
  ONE_DECIMAL_FORMAT_COLUMNS,
  TWO_DECIMAL_FORMAT_COLUMNS,
  ZERO_DECIMAL_FORMAT_COLUMNS_WITH_PERCENT,
  ONE_DECIMAL_FORMAT_COLUMNS_WITH_PERCENT,
  TWO_DECIMAL_FORMAT_COLUMNS_WITH_PERCENT
} from '../../utils/Constants';
import { useSelector } from 'react-redux';

const ReviewItemsCellContent = ({
  columns = [],
  column,
  row,
  isEditable,
  originalData,
  onValueChange,
  editCellData = [],
  onEditCell,
  tableName,
  data = [],
  renderFlag
}) => {
  const [value, setValue] = useState(row[column.id]);
  const reviewItemsTableData = useSelector((state) => state?.portfolioMaster?.reviewItemsTableData);

  const getEditableCells = () => {};
  useEffect(() => {
    setValue(row[column.id]);
  }, [row[column.id], renderFlag]);

  const columnType = column?.type;
  const columnEditable = column?.editable;
  const columnOptions = column?.options;

  if (columnType === 'text' && isEditable && columnEditable) {
    if (column?.id === 'LaunchPeriodValue') {
      return (
        <TextField
          id={column.id}
          placeholder="YYYYPP"
          size="small"
          value={value}
          onChange={(e) => {
            const newValue = e.target.value;
            // Check if value is a number and does not exceed 6 digits
            if (/^\d{0,6}$/.test(newValue)) {
              if (newValue.length === 6) {
                const year = parseInt(newValue.substring(0, 4));
                const month = parseInt(newValue.substring(4, 6));

                if (year >= 2000 && year <= 2100 && month >= 1 && month <= 13) {
                  // Get Row value from redux state using unique TPOSKU to validate
                  const result = reviewItemsTableData.find((item) => item.TPOSKU === row?.TPOSKU);
                  if (result?.DiscontinuedPeriodValue.length > 0) {
                    if (year > Number(result?.DiscontinuedPeriodValue.slice(0, 4))) {
                      triggerNotification(
                        'Launch Period should be less than or equal to Discontinued Period',
                        'error'
                      );
                      // If the value is invalid, prevent update
                      setValue(''); // Remove the invalid character
                    } else if (year == Number(result?.DiscontinuedPeriodValue.slice(0, 4))) {
                      if (month > Number(result?.DiscontinuedPeriodValue.slice(-2))) {
                        triggerNotification(
                          'Launch Period should be less than or equal to Discontinued Period',
                          'error'
                        );
                        // If the value is invalid, prevent update
                        setValue(''); // Remove the invalid character
                      } else {
                        // If the value is valid, call onEditCell and update the state
                        onEditCell(row?.TPOSKU, column?.id, newValue, row);
                        setValue(newValue);
                      }
                    } else {
                      // If the value is valid, call onEditCell and update the state
                      onEditCell(row?.TPOSKU, column?.id, newValue, row);
                      setValue(newValue);
                    }
                  } else {
                    // If the value is valid, call onEditCell and update the state
                    onEditCell(row?.TPOSKU, column?.id, newValue, row);
                    setValue(newValue);
                  }
                } else {
                  triggerNotification(
                    'Launch Period should be in the format YYYYPP, Where YYYY should be in the range of (2000-2100) and PP can be in the range of (01-13).',
                    'error'
                  );
                  // If the value is invalid, prevent update
                  setValue(''); // Remove the invalid character
                }
              } else if (newValue === '') {
                onEditCell(row?.TPOSKU, column?.id, newValue, row);
                setValue(newValue);
              } else {
                // For values less than 6 digits, simply update the state
                setValue(newValue);
              }
            } else if (newValue === '') {
              onEditCell(row?.TPOSKU, column?.id, newValue, row);
              setValue(newValue);
            } else {
              let numVal = newValue.replace(/\D/g, '');
              setValue(numVal);
            }
          }}
          inputProps={{
            maxLength: 6 // Limit input to 6 characters
          }}
          InputProps={
            row[column?.id]?.length <= 0
              ? {
                  endAdornment: (
                    <InputAdornment position="end">
                      <WarningAmberOutlinedIcon
                        sx={{
                          color: '#FF891C',
                          width: '16px',
                          height: '16px',
                          marginRight: '4px'
                        }}
                      />
                    </InputAdornment>
                  )
                }
              : ''
          }
        />
      );
    } else if (column?.id === 'DiscontinuedPeriodValue') {
      return (
        <TextField
          id={column.id}
          placeholder="YYYYPP"
          size="small"
          value={value}
          onChange={(e) => {
            const newValue = e.target.value;
            // Check if value is a number and does not exceed 6 digits
            if (/^\d{0,6}$/.test(newValue)) {
              if (newValue.length === 6) {
                const year = parseInt(newValue.substring(0, 4));
                const month = parseInt(newValue.substring(4, 6));

                if (year >= 2000 && year <= 2100 && month >= 1 && month <= 13) {
                  // Get Row value from redux state using unique TPOSKU to validate
                  const result = reviewItemsTableData.find((item) => item.TPOSKU === row?.TPOSKU);
                  if (result?.LaunchPeriodValue?.length > 0) {
                    if (year < Number(result?.LaunchPeriodValue.slice(0, 4))) {
                      triggerNotification(
                        'Discontinued Period should be greater than or equal to Launch Period',
                        'error'
                      );
                      // If the value is invalid, prevent update
                      setValue(''); // Remove the invalid character
                    } else if (year == Number(result?.LaunchPeriodValue.slice(0, 4))) {
                      if (month < Number(result?.LaunchPeriodValue.slice(-2))) {
                        triggerNotification(
                          'Discontinued Period should be greater than or equal to Launch Period',
                          'error'
                        );
                        // If the value is invalid, prevent update
                        setValue(''); // Remove the invalid character
                      } else {
                        // If the value is valid, call onEditCell and update the state
                        onEditCell(row?.TPOSKU, column?.id, newValue, row);
                        setValue(newValue);
                      }
                    } else {
                      // If the value is valid, call onEditCell and update the state
                      onEditCell(row?.TPOSKU, column?.id, newValue, row);
                      setValue(newValue);
                    }
                  } else {
                    // If the value is valid, call onEditCell and update the state
                    onEditCell(row?.TPOSKU, column?.id, newValue, row);
                    setValue(newValue);
                  }
                } else {
                  triggerNotification(
                    'Discontinued Period should be in the format YYYYPP, Where YYYY should be in the range of (2000-2100) and PP should be in the range of (01-13).',
                    'error'
                  );
                  // If the value is invalid, prevent update
                  setValue(''); // Remove the invalid character
                }
              } else if (newValue === '') {
                onEditCell(row?.TPOSKU, column?.id, newValue, row);
                setValue(newValue);
              } else {
                // For values less than 6 digits, simply update the state
                setValue(newValue);
              }
            } else if (newValue === '') {
              onEditCell(row?.TPOSKU, column?.id, newValue, row);
              setValue(newValue);
            } else {
              let numVal = newValue.replace(/\D/g, '');
              setValue(numVal);
            }
          }}
          inputProps={{
            maxLength: 6 // Limit input to 6 characters
          }}
          InputProps={
            row[column?.id]?.length <= 0
              ? {
                  endAdornment: (
                    <InputAdornment position="end">
                      <WarningAmberOutlinedIcon
                        sx={{
                          color: '#FF891C',
                          width: '16px',
                          height: '16px',
                          marginRight: '4px'
                        }}
                      />
                    </InputAdornment>
                  )
                }
              : ''
          }
        />
      );
    } else {
      return (
        <TextField
          id={column.id}
          placeholder="Fill info"
          size="small"
          value={editCellData?.length > 0 ? getEditableCells() : value}
          onChange={(e) => onEditCell(row?.TPOSKU, column?.id, e.target.value, row)}
          InputProps={
            row[column?.id]?.length <= 0
              ? {
                  endAdornment: (
                    <InputAdornment position="end">
                      <WarningAmberOutlinedIcon
                        sx={{
                          color: '#FF891C',
                          width: '16px',
                          height: '16px',
                          marginRight: '4px'
                        }}
                      />
                    </InputAdornment>
                  )
                }
              : ''
          }
        />
      );
    }
  } else if (
    columnType === 'dropdown' &&
    isEditable &&
    columnEditable
    // && columnOptions?.length > 0
    // && checkIsEmpty(row.id, column.id)
  ) {
    return (
      <Select
        size="small"
        labelId={`select-label-${column.TPOSKU}`}
        id={`select-${column.TPOSKU}`}
        name={column.TPOSKU}
        // value={originalData[column.TPOSKU] ?? ''}
        // value={!(editCellData.length) > 0 ? value : getEditableCells()}
        // value={editCellData.length > 0 ? getEditableCells() : value}
        value={value}
        // label={'YYYYPP'}
        // onChange={handleNewRowChange} // Add onChange event
        // onChange={(e) => onEditCell(row?.TPOSKU, column?.id, e.target.value)}

        onChange={(e) => {
          const newValue = e.target.value;
          onEditCell(row?.TPOSKU, column?.id, newValue, row);
          // If you need to update the local state after selection:
          setValue(newValue); // Assuming setValue is a state setter function
        }}
        sx={{ width: '8rem' }}>
        {columnOptions?.map((val) => (
          <MenuItem key={val} value={val} style={{ height: '30px' }}>
            {val}
          </MenuItem>
        ))}
      </Select>
    );
  } else if (columnType === 'date') {
    let formattedDate = '';

    const formatDate = (timestamp) => {
      const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const date = new Date(timestamp * 1000); // Convert from epoch (in seconds) to milliseconds
      const timeZoneAbbreviation = format(date, 'zzz', { timeZone });
      if (!isNaN(date.getTime())) {
        const options = {
          timeZone,
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit'
        };
        const formattedDate = date.toLocaleString('en-US', options);
        const [datePart, timePart] = formattedDate.split(', ');
        const [time, modifier] = timePart.split(' ');
        const [hours, minutes] = time.split(':');
        return `${datePart} ${hours}:${minutes} ${modifier} ${timeZoneAbbreviation}`;
      }
      return 'Invalid Date';
    };

    // Check which field is being processed and format accordingly
    if (row.lastModifiedSince && column.id === 'lastModifiedSince') {
      formattedDate = formatDate(row.lastModifiedSince);
    } else if (row.cycleStartTime && column.id === 'cycleStartTime') {
      formattedDate = formatDate(row.cycleStartTime);
    } else if (row.cycleEndTime && column.id === 'cycleEndTime') {
      formattedDate = formatDate(row.cycleEndTime);
    } else if (row.LastUpdatedTimestamp && column.id == 'LastUpdatedTimestamp') {
      formattedDate = formatDate(row.LastUpdatedTimestamp);
    }

    return formattedDate;
  } else if (columnType === 'number') {
    if (DOLLAR_FORMAT_COLUMNS.indexOf(column.label) > -1) {
      return convertToCurrency(value);
    }
    if (COMMA_FORMAT_COLUMNS.indexOf(column.label) > -1) {
      return convertToCurrencyWithoutSign(value);
    }
    if (ZERO_DECIMAL_FORMAT_COLUMNS_WITH_PERCENT.indexOf(column.label) > -1) {
      return convertToPercentageWithDecimal(value, 0);
    }
    if (ONE_DECIMAL_FORMAT_COLUMNS_WITH_PERCENT.indexOf(column.label) > -1) {
      return convertToPercentageWithDecimal(value, 1);
    }
    if (TWO_DECIMAL_FORMAT_COLUMNS_WITH_PERCENT.indexOf(column.label) > -1) {
      return convertToPercentageWithDecimal(value, 2);
    }
    if (ZERO_DECIMAL_FORMAT_COLUMNS.indexOf(column.label) > -1) {
      return convertToRoundOffWithNumberOfDecimals(value, 0);
    }
    if (ONE_DECIMAL_FORMAT_COLUMNS.indexOf(column.label) > -1) {
      return convertToRoundOffWithNumberOfDecimals(value, 1);
    }
    if (TWO_DECIMAL_FORMAT_COLUMNS.indexOf(column.label) > -1) {
      return convertToRoundOffWithNumberOfDecimals(value, 2);
    }
  }
  return value;
};

export default ReviewItemsCellContent;
